import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotifications } from '../context/NotificationContext';
import { log, error as errorLog } from '../utils/logger';
import { getUserData } from '../utils/encryption';
import { Notification } from '../types/notifications';
import { createRoot } from 'react-dom/client';
import React from 'react';
import { ThemeProvider } from '@mui/material';
import { lightTheme, darkTheme } from '../theme/theme';
import { NotificationAlert } from '../components/common/NotificationAlert';
import { getAuthHeaders } from '../services/api';
import { AnaSolutionDialogV2 } from 'components/SAST/NewScan/AnaSolutionDialogV2';

interface WebSocketNotification {
  type: string;
  data: {
    user_id: number;
    type: string;
    scan_type: string;
    message: string;
    created_at: string;
    data: {
      scan_id?: number;
      finding_id?: number;
    };
    id: number;
    is_read: number;
  };
}

export const useWebSocketNotifications = () => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectWebSocketRef = useRef<(() => void) | null>(null);
  const notificationRootRef = useRef<any | null>(null);
  const notificationElementRef = useRef<HTMLDivElement | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { addNotification, markAsRead } = useNotifications();
  
  // Get current theme from localStorage
  const getCurrentTheme = () => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    return savedTheme === 'dark' ? darkTheme : lightTheme;
  };

  const renderNotification = useCallback((notification: Notification) => {
    // Create a container for the notification if it doesn't exist
    let notificationContainer = document.getElementById('notification-alert-container');
    if (!notificationContainer) {
      notificationContainer = document.createElement('div');
      notificationContainer.id = 'notification-alert-container';
      document.body.appendChild(notificationContainer);
    }

    // Clean up previous notification if exists
    if (notificationRootRef.current && notificationElementRef.current) {
      notificationRootRef.current.unmount();
      notificationElementRef.current.remove();
    }

    // Create a new div for this specific notification
    const notificationElement = document.createElement('div');
    notificationContainer.appendChild(notificationElement);
    notificationElementRef.current = notificationElement;

    // Create a root and render the NotificationAlert
    const root = createRoot(notificationElement);
    notificationRootRef.current = root;

    root.render(
      React.createElement(
        ThemeProvider,
        { theme: getCurrentTheme() },
        React.createElement(NotificationAlert, {
          notification: notification,
          onClose: () => {
            root.unmount();
            notificationElement.remove();
          },
          onMarkAsRead: async () => {
            try {
              await markAsRead(notification.id);
              root.unmount();
              notificationElement.remove();
            } catch (err: any) {
              errorLog('Failed to mark notification as read:', err);
            }
          },
          onSeeMore: () => {
            try {
              if (notification.scan_type) {
                let data;
                try {
                  data = typeof notification.data === 'string'
                    ? JSON.parse(notification.data)
                    : notification.data;
                } catch (err: any) {
                  errorLog('Failed to parse notification data:', err);
                  return;
                }

                if (!data?.scan_id) {
                  log('Missing scan ID in notification:', notification);
                  return;
                }

                const scanType = notification.scan_type?.toLowerCase();
                let route = '';

                switch (scanType) {
                  case 'sbom':
                    route = `/sbom/scan/${data.scan_id}`;
                    break;
                  case 'sca':
                    route = `/sca/scan/${data.scan_id}`;
                    break;
                  case 'secrets':
                  case 'secret':
                    route = `/secrets/scan/${data.scan_id}`;
                    break;
                  case 'api':
                  case 'api-security':
                    route = `/api-security/scan/${data.scan_id}`;
                    break;
                  case 'threat-model':
                  case 'threat_model':
                    route = `/threat-modeling/scan/${data.scan_id}`;
                    break;
                  case 'sast':
                    route = `/sast/scan/${data.scan_id}`;
                    break;
                  case 'dast':
                    route = `/dast/scan/${data.scan_id}`;
                    break;
                  case 'infra':
                  case 'infrastructure':
                    route = `/infra/scan/${data.scan_id}`;
                    break;
                  case 'ana':
                    let anaContainer = document.getElementById('ana-dialog-container');
                    if (!anaContainer) {
                      anaContainer = document.createElement('div');
                      anaContainer.id = 'ana-dialog-container';
                      document.body.appendChild(anaContainer);
                    }
                    
                    const anaRoot = createRoot(anaContainer);
                    anaRoot.render(
                      React.createElement(
                        ThemeProvider,
                        { theme: getCurrentTheme() },
                        React.createElement(AnaSolutionDialogV2, {
                          open: true,
                          onClose: () => {
                            anaRoot.unmount();
                            anaContainer?.remove();
                          },
                          finding_id: data.finding_id || 0
                        })
                      )
                    );
                    return;
                }

                if (route) {
                  window.location.href = route;
                  root.unmount();
                  notificationElement.remove();
                }
              }
            } catch (err) {
              errorLog('Error during notification redirection:', err);
            } finally {
              root.unmount();
              notificationElement.remove();
            }
          }
        })
      )
    );

    // Auto-dismiss after 10 seconds
    setTimeout(() => {
      if (root) {
        root.unmount();
        notificationElement.remove();
      }
    }, 10000);
  }, [markAsRead]);

  const scheduleReconnect = useCallback(() => {
    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Schedule reconnect after 5 seconds
    reconnectTimeoutRef.current = setTimeout(() => {
      log('Attempting to reconnect WebSocket...');
      connectWebSocketRef.current?.();
    }, 5000);
  }, []);

  const connectWebSocket = useCallback(() => {
    try {
      // Skip connection if we're on login page
      if (window.location.pathname === '/login') {
        return;
      }

      // Skip if we already have an active connection
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        log('WebSocket connection already established, skipping reconnect');
        return;
      }

      // Get auth headers
      const headers = getAuthHeaders();
      if (!headers) {
        log('No auth headers available, skipping WebSocket connection');
        return;
      }

      // Get user data
      const userData = getUserData();
      if (!userData || !userData.user_id) {
        log('No user data available, skipping WebSocket connection');
        return;
      }

      // Extract token from Authorization header
      const token = headers.Authorization.replace('Bearer ', '');
      const orgId = headers['x-org-id'] || userData.org_names_list?.[0] || userData.email || 'OpenAna';
      const userId = headers['x-user-id'] || userData.user_id.toString();

      // Close existing connection if any
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        wsRef.current.close();
      }

      // Create WebSocket connection with auth parameters
      const wsUrl = `${process.env.REACT_APP_WEBSOCKET_URL || 'wss://serenity.medista.ai:9022'}/?token=${token}&org=${orgId}&user=${userId}`;
      log('Connecting to WebSocket:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        log('WebSocket connection established');
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as WebSocketNotification;
          log('WebSocket notification received:', data);

          if (data.type === 'notification') {
            // Convert WebSocket notification to app notification format
            const notification: Notification = {
              id: data.data.id,
              user_id: data.data.user_id,
              type: data.data.type,
              scan_type: data.data.scan_type,
              message: data.data.message,
              created_at: data.data.created_at,
              updated_at: data.data.created_at, // Use created_at as updated_at
              is_read: data.data.is_read,
              data: {
                scan_id: data.data.data.scan_id || null,
                finding_id: data.data.data.finding_id || null
              }
            };

            // Add notification to context
            addNotification(notification);

            // Store the latest notification ID in localStorage
            const lastStoredId = parseInt(localStorage.getItem('lastNotification') || '0');
            if (notification.id > lastStoredId) {
              localStorage.setItem('lastNotification', notification.id.toString());
            }

            // Show notification alert
            if (!notification.is_read) {
              renderNotification(notification);
            }
          }
        } catch (err) {
          errorLog('Error processing WebSocket message:', err);
        }
      };

      ws.onerror = (err) => {
        errorLog('WebSocket error:', err);
        setIsConnected(false);
        scheduleReconnect();
      };

      ws.onclose = (event) => {
        log(`WebSocket connection closed: ${event.code} ${event.reason}`);
        setIsConnected(false);
        scheduleReconnect();
      };
    } catch (err) {
      errorLog('Error establishing WebSocket connection:', err);
      setIsConnected(false);
      scheduleReconnect();
    }
  }, [addNotification, renderNotification, scheduleReconnect]);

  connectWebSocketRef.current = connectWebSocket;

  useEffect(() => {
    // Initial connection
    connectWebSocket();

    // Cleanup on unmount
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (notificationRootRef.current && notificationElementRef.current) {
        notificationRootRef.current.unmount();
        notificationElementRef.current.remove();
      }
    };
  }, [connectWebSocket]);

  // Listen for auth changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken' || e.key === 'userData') {
        // Reconnect WebSocket when auth changes
        connectWebSocket();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connectWebSocket]);

  return { isConnected };
};